import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback } from 'react';

// third-party
import { Chance } from 'chance';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project import
import Loader from 'components/Loader';
import axios from 'utils/axios';
import ApiService from 'services/ApiService';
import AuthService from 'services/AuthService';
import { setupAutoLogout } from 'utils/AutoLogout';

const chance = new Chance();

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// 토큰 세션 설정 - serviceToken과 refreshToken을 모두 관리
const setSession = (serviceToken, refreshToken = null) => {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    // x-access-token 헤더만 사용하도록 통일
    axios.defaults.headers.common['x-access-token'] = serviceToken;
    
    // refreshToken이 제공된 경우에만 저장
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  } else {
    localStorage.removeItem('serviceToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['x-access-token'];
    delete axios.defaults.headers.common.Authorization;
  }
};

// 인증 데이터 완전 삭제 - 모든 토큰과 사용자 정보 제거
const clearAuthData = () => {
  localStorage.removeItem('serviceToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  sessionStorage.clear();
  delete axios.defaults.headers.common['x-access-token'];
  delete axios.defaults.headers.common.Authorization;
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearAuthData();
      dispatch({ type: LOGOUT });
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem('serviceToken');
        if (serviceToken) {
          setSession(serviceToken);

          // 사용자 정보 조회 (토큰 유효성 검증)
          const response = await ApiService.get({ path: '/auth/userInfo' });

          // 응답 상태와 데이터 검증
          if (response && response.status === 200 && response.data && response.data.user) {
            const { user } = response.data;
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user
              }
            });
          } else {
            // 토큰이 유효하지 않거나 사용자 정보를 가져올 수 없는 경우
            console.warn('Invalid token or user data, clearing session');
            throw new Error('Invalid user data received');
          }
        } else {
          clearAuthData();
          dispatch({
            type: LOGOUT
          });
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        // 401 또는 토큰 관련 오류 시 세션 정리
        if (err.response?.status === 401 || err.message.includes('token')) {
          console.warn('Token authentication failed, clearing session');
        }
        clearAuthData();
        dispatch({
          type: LOGOUT
        });
      } finally {
        dispatch({ type: 'INITIALIZED' });
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (state.isLoggedIn && state.isInitialized) {
      const cleanupAutoLogout = setupAutoLogout(logout);
      return () => cleanupAutoLogout();
    }
  }, [state.isLoggedIn, state.isInitialized, logout]);

  // 로그인 처리 - serviceToken과 refreshToken을 모두 저장
  const login = async (email, password) => {
    const response = await AuthService.login(email, password);

    if (response && response.status === 200 && response.data) {
      const { serviceToken, refreshToken, user } = response.data;

      // refreshToken을 포함하여 세션 설정
      setSession(serviceToken, refreshToken);
      
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user
        }
      });
    } else {
      throw new Error('Login failed: Invalid response');
    }
  };

  const register = async (email, password, firstName, lastName) => {
    // todo: this flow need to be recode as it not verified
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });
    let users = response.data;

    if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
      const localUsers = window.localStorage.getItem('users');
      users = [
        ...JSON.parse(localUsers),
        {
          id,
          email,
          password,
          name: `${firstName} ${lastName}`
        }
      ];
    }

    window.localStorage.setItem('users', JSON.stringify(users));
  };

  const resetPassword = async (email) => {
    const response = await AuthService.resetPassword(email);
    return response;
  };

  const updateProfile = async (objData) => {
    const response = await AuthService.getProfile(objData);
    return response;
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return <JWTContext.Provider value={{ ...state, login, logout, register, resetPassword, updateProfile }}>{children}</JWTContext.Provider>;
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;
